import { createApp, h, ref } from 'vue'
import type { App, DirectiveBinding, Ref } from 'vue'
import type { Placement } from '@floating-ui/vue'
import { DEFAULT_DELAY_MS, TooltipTriggers, type TooltipVariant } from '../ui-components/DTTooltip/types'
import DTTooltipDirective from '../ui-components/DTTooltip/DTTooltipDirective.vue'
import { defineNuxtPlugin } from '#app'

const placements = ['top', 'right', 'bottom', 'left'].reduce((acc: string[], cur) => [...acc, ...[cur, `${cur}-end`, `${cur}-start`]], [])

interface Directive {
  target: HTMLElement
  itemId: string
  content: string
  options: {
    trigger?: Array<keyof typeof TooltipTriggers>
    placement?: Placement
    variant?: keyof typeof TooltipVariant
    title?: string
    delay?: number
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  let tooltipApp: App
  let directives: Ref<Directive[]>

  const createTooltipApp = () => {
    if (tooltipApp)
      return

    directives = ref([])

    tooltipApp = createApp({
      name: 'DTTooltipApp',
      setup() {
        return {
          directives,
        }
      },
      render() {
        return this.directives.map(({ itemId, content, ...rest }: Directive) => content ? h(DTTooltipDirective, { ...rest, content, key: itemId }) : null)
      },
    })

    const mountTarget = document.createElement('div')
    mountTarget.id = 'dt-tooltip-container'
    mountTarget.classList.add(...['antialiased', 'h-0'])
    document.body.appendChild(mountTarget)
    tooltipApp.mount(mountTarget)
  }

  const createTooltip = (
    target: HTMLElement,
    value: string,
    options: Directive['options'],
  ) => {
    createTooltipApp()

    if (target.dataset.dttooltip) {
      directives.value = directives.value.map((item) => {
        if (item.itemId === target.dataset.dttooltip)
          return { ...item, content: value, options }

        return item
      })
      return
    }

    const itemId = `DTTooltipTarget-${crypto.randomUUID()}`
    target.dataset.dttooltip = itemId
    directives.value.push({ target, itemId, content: value, options })
  }

  const bind = (el: HTMLElement, binding: DirectiveBinding) => {
    if (!binding.value)
      return

    let content = ''
    const options: Directive['options'] = {}

    if (typeof binding.value === 'string') {
      const modifiers = Object.keys(binding.modifiers)
      const boundTrigger = modifiers.filter(x => Object.keys(TooltipTriggers).includes(x))

      options.trigger = boundTrigger.length ? (boundTrigger as typeof options.trigger) : undefined
      options.placement = modifiers.filter(x => (placements).includes(x))?.[0] as Placement
      options.variant = modifiers.filter(x => ['light', 'dark'].includes(x))?.[0] as typeof options.variant
      content = binding.value
      options.delay = modifiers.filter(x => !Number.isNaN(Number(x))).map(Number)[0] ?? DEFAULT_DELAY_MS
    }

    else if (typeof binding.value === 'object') {
      Object.assign(options, binding.value)
      content = binding.value?.content
    }

    createTooltip(el, content, options)
  }

  const destroyTooltip = (el: HTMLElement) => {
    if (!el.dataset.dttooltip)
      return

    directives.value = directives.value.filter(d => d.itemId !== el.dataset.dttooltip)

    delete el.dataset.dttooltip
  }

  const tooltip = {
    beforeMount: bind,
    updated: bind,
    beforeUnmount(el: HTMLElement) {
      destroyTooltip(el)
    },
  }

  nuxtApp.vueApp.directive('dt-tooltip', tooltip)
})
